import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { config } from "../../Helpers/env";
import MainSnackbar from "../Utils/Snackbar/SmsSnackbar";
import { Container } from "../Utils/styledComponents";
import PageTitle from "../Utils/smsTitle";
import { SubHeader } from "../../Containers/SubHeader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMoneyBill,
  faFileExcel,
  faFilePdf
} from "@fortawesome/free-solid-svg-icons";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import * as snackbarActions from "../../actions/snackbarActions";
import CustomTable from "../Utils/Tables/CustomTable";
import { TableBody, TableRow, TableCell } from "@material-ui/core";
import moment from "moment";
import {
  BarChart,
  Bar,
  ResponsiveContainer,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as BarTooltip
} from "recharts";
import { BackButton } from "../Utils/Buttons/DataExportationButton";
import {
  useExcelReports,
  usePdfDownload
} from "../Utils/ReusableComponents/useExcelExport";

const ExpenseReport = (props) => {
  const { actions, snackbars } = props;

  const [dateRange, setDateRange] = useState({
    start_date: moment().format("YYYY-MM-DD"),
    end_date: moment().format("YYYY-MM-DD")
  });
  const { start_date, end_date } = dateRange;

  const [activeTab, setActiveTab] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({
    expenses: [],
    grand_total_base: 0,
    grand_total_by_currency: [],
    by_expense_account: [],
    period: {}
  });

  const {
    expenses,
    grand_total_base,
    grand_total_by_currency,
    by_expense_account
  } = data;

  const changeDate = (e) => {
    setDateRange({ ...dateRange, [e.target.name]: e.target.value });
  };

  const fetchReport = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${config.smsUrl}/accounting/expenses/report`,
        {
          params: { start_date, end_date, search: searchTerm || undefined }
        }
      );
      setData(
        res.data.data || {
          expenses: [],
          grand_total_base: 0,
          grand_total_by_currency: [],
          by_expense_account: [],
          period: {}
        }
      );
    } catch (e) {
      console.error(e);
      actions.snackbarActions.snackError?.("Error loading report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchReport();
    }, 500);
    return () => clearTimeout(timer);
  }, [start_date, end_date, searchTerm]);

  const headData = [
    { id: "expense_account_name", label: "Account Name", numeric: false },
    { id: "currency_symbol", label: "Currency", numeric: false },
    { id: "line_count", label: "Lines", numeric: false },
    { id: "total_original", label: "Total Original", numeric: false },
    { id: "total_base", label: "Total (USD)", numeric: false }
  ];

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const exportParams = {
    start_date,
    end_date,
    search: searchTerm || undefined
  };

  const { exportExcel, isLoading: isLoadingExcel } = useExcelReports(actions, {
    file_name: `Expense_Report_${start_date}_to_${end_date}`,
    params: { ...exportParams, format: "excel" },
    url: "accounting/expenses/report/export"
  });

  const { exportPdf, isLoading: isLoadingPdf } = usePdfDownload(actions, {
    file_name: `Expense_Report_${start_date}_to_${end_date}`,
    params: { ...exportParams, format: "pdf" },
    url: "accounting/expenses/report/export"
  });

  const dailyTrend = useMemo(() => {
    const map = new Map();
    (expenses || []).forEach((exp) => {
      const d = exp.date ? exp.date.substring(0, 10) : "Unknown";
      if (!map.has(d)) map.set(d, { date: d, total: 0 });
      map.get(d).total += Number(exp.amount_in_base_currency || 0);
    });
    return Array.from(map.values()).sort((a, b) =>
      a.date.localeCompare(b.date)
    );
  }, [expenses]);

  const formatCurrency = (val) =>
    Number(val || 0).toLocaleString(undefined, { minimumFractionDigits: 2 });

  return (
    <>
      <PageTitle title="Expenses Report" />
      <SubHeader title="Accounting" subTitle="Expenses Report">
        <FontAwesomeIcon icon={faMoneyBill} />
      </SubHeader>

      <Container className="mt-1">
        <MainSnackbar
          open={snackbars.openBar}
          variant={snackbars.type}
          message={snackbars.message}
          handleCloseBar={actions.snackbarActions.hideSnackbar}
        />

        {/* ===== TOP SECTION ===== */}
        <div style={{ padding: "8px 0 0 0" }}>
          {/* Back + Export */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              marginBottom: "12px",
              gap: "8px"
            }}
          >
            <BackButton to="/reports/generalreports" text="Accounting" />

            <div style={{ display: "flex", gap: "8px" }}>
              <button
                type="button"
                onClick={exportExcel}
                disabled={isLoadingExcel}
                className="btn btn-sm text-white"
                style={{
                  backgroundColor: "#389081",
                  borderColor: "#389081",
                  height: "32px",
                  padding: "0 14px",
                  fontSize: "13px",
                  borderRadius: "6px"
                }}
              >
                <FontAwesomeIcon icon={faFileExcel} className="me-1" />
                {isLoadingExcel ? "Exporting..." : "Excel"}
              </button>

              <button
                type="button"
                onClick={exportPdf}
                disabled={isLoadingPdf}
                className="btn btn-sm text-white"
                style={{
                  backgroundColor: "#389081",
                  borderColor: "#389081",
                  height: "32px",
                  padding: "0 14px",
                  fontSize: "13px",
                  borderRadius: "6px"
                }}
              >
                <FontAwesomeIcon icon={faFilePdf} className="me-1" />
                {isLoadingPdf ? "Exporting..." : "PDF"}
              </button>
            </div>
          </div>

          {/* Date + Search filters – tight */}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: "10px",
              marginBottom: "14px"
            }}
          >
            <input
              type="date"
              name="start_date"
              value={start_date}
              onChange={changeDate}
              className="form-control form-control-sm"
              style={{
                width: "145px",
                height: "32px",
                padding: "2px 8px",
                fontSize: "13px",
                borderRadius: "6px"
              }}
            />
            <input
              type="date"
              name="end_date"
              value={end_date}
              onChange={changeDate}
              className="form-control form-control-sm"
              style={{
                width: "145px",
                height: "32px",
                padding: "2px 8px",
                fontSize: "13px",
                borderRadius: "6px"
              }}
            />
            <input
              type="text"
              placeholder="Search account..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="form-control form-control-sm"
              style={{
                minWidth: "180px",
                height: "32px",
                padding: "2px 8px",
                fontSize: "13px",
                borderRadius: "6px"
              }}
            />
          </div>
        </div>

        {/* Summary Cards – system colors */}
        <div className="row g-3 mb-4">
          <div className="col-6 col-md-3">
            <div
              className="card border-0 shadow-sm text-white"
              style={{
                background: "linear-gradient(135deg, #389081, #027a5f)",
                borderRadius: "10px"
              }}
            >
              <div className="card-body text-center py-3">
                <h6 className="mb-1 opacity-75" style={{ fontSize: "12px" }}>
                  Total Expenses (USD)
                </h6>
                <h3 className="mb-0 fw-bold" style={{ fontSize: "1.4rem" }}>
                  {formatCurrency(grand_total_base)}
                </h3>
              </div>
            </div>
          </div>

          {(grand_total_by_currency || []).map((curr, idx) => (
            <div key={idx} className="col-6 col-md-3">
              <div
                className="card border-0 shadow-sm text-white"
                style={{
                  background:
                    idx % 2 === 0
                      ? "linear-gradient(135deg, #08819c, #0a6b7a)"
                      : "linear-gradient(135deg, #F76B1D, #e05a0d)",
                  borderRadius: "10px"
                }}
              >
                <div className="card-body text-center py-3">
                  <h6 className="mb-1 opacity-75" style={{ fontSize: "12px" }}>
                    Total in {curr.symbol || "Unknown"}
                  </h6>
                  <h3 className="mb-0 fw-bold" style={{ fontSize: "1.4rem" }}>
                    {formatCurrency(curr.total)}
                  </h3>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <ul className="nav nav-tabs mb-3">
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 0 ? "active fw-bold" : ""}`}
              onClick={() => setActiveTab(0)}
              style={{ fontSize: "14px" }}
            >
              Tabular View
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 1 ? "active fw-bold" : ""}`}
              onClick={() => setActiveTab(1)}
              style={{ fontSize: "14px" }}
            >
              Graphical View
            </button>
          </li>
        </ul>

        {/* Content */}
        {activeTab === 0 ? (
          <div
            className="bg-white"
            style={{ borderRadius: "10px", overflow: "hidden" }}
          >
            <CustomTable
              title="Expenses by Category"
              handleChangePage={handleChangePage}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
              term={searchTerm}
              headData={headData}
              handler={(e) => setSearchTerm(e.target.value)}
              page={page}
              rowsPerPage={rowsPerPage}
              data={by_expense_account}
              colSpan={5}
            >
              {loading ? (
                <TableBody>
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      Loading...
                    </TableCell>
                  </TableRow>
                </TableBody>
              ) : by_expense_account && by_expense_account.length > 0 ? (
                <TableBody>
                  {by_expense_account
                    .slice(
                      page * rowsPerPage,
                      page * rowsPerPage + rowsPerPage
                    )
                    .map((item, idx) => (
                      <TableRow key={idx} hover>
                        <TableCell>
                          {item.expense_account_name || "-"}
                        </TableCell>
                        <TableCell>{item.currency_symbol || "-"}</TableCell>
                        <TableCell>{item.line_count || 0}</TableCell>
                        <TableCell>
                          {item.currency_symbol}{" "}
                          {formatCurrency(item.total_original)}
                        </TableCell>
                        <TableCell>
                          USD {formatCurrency(item.total_base)}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              ) : (
                <TableBody>
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      No records found
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}
            </CustomTable>
          </div>
        ) : (
          <div
            className="card border-0 shadow-sm"
            style={{ borderRadius: "10px" }}
          >
            <div className="card-body">
              <h6 className="mb-3 fw-bold">Daily Trend</h6>
              {loading ? (
                <div className="text-center py-5">Loading...</div>
              ) : dailyTrend.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  No daily data available
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart
                    data={dailyTrend}
                    margin={{ top: 10, right: 30, left: 20, bottom: 60 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis
                      dataKey="date"
                      angle={-45}
                      textAnchor="end"
                      height={70}
                      tick={{ fontSize: 12 }}
                    />
                    <YAxis tickFormatter={(val) => val.toLocaleString()} />
                    <BarTooltip
                      formatter={(val) => [formatCurrency(val), "Total"]}
                    />
                    <Bar
                      dataKey="total"
                      fill="#389081"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        )}
      </Container>
    </>
  );
};

const mapStateToProps = (state) => ({ snackbars: state.snackbar });
const mapDispatchToProps = (dispatch) => ({
  actions: { snackbarActions: bindActionCreators(snackbarActions, dispatch) }
});

export default connect(mapStateToProps, mapDispatchToProps)(ExpenseReport);